/**
 * Notificação do chat no aparelho — duas peças:
 *
 *  - `<GuardiaoDasNotificacoes />` (montado uma vez, com o app): ao entrar,
 *    acerta a inscrição deste navegador com quem está logado e, quando a
 *    pessoa toca no aviso com o app aberto, abre a conversa;
 *  - `<ControleDeNotificacoes />` (na engrenagem do Chat): o botão que LIGA e DESLIGA. O
 *    navegador só deixa pedir a permissão a partir de um clique;
 *  - `<ConviteDosAvisosNoDesktop />` (no cabeçalho, 2026-10-28): quem nunca
 *    ligou vê, em qualquer tela, o convite para ligar — antes era preciso
 *    entrar no Chat e achar o botão, e quase ninguém ligava.
 */

import { useEffect, useRef, useState } from "react";
import { FiBell, FiX } from "react-icons/fi";
import { useLocation, useNavigate } from "react-router-dom";
import styled from "styled-components";

import { useAuth } from "../../context/AuthContext";
import { useToast } from "../../context/ToastContext";
import {
  acertarComQuemEstaAqui,
  desligar,
  dispensarConvite,
  dispensouConvite,
  EVENTO_AVISOS_MUDARAM,
  estadoAtual,
  ligar,
  navegadorDoComputador,
  recontarNaoLidasDoChat,
  SEGUNDO_PLANO,
} from "../../services/notificacoesDoChat";
import { copiarTexto } from "../../services/chat";

/** Fora da página do Chat, o número do botão não fica mais velho que isto. */
const RECONTAGEM_DO_CHAT_MS = 20000;

const EXPLICACAO = {
  sem_suporte:
    "Este navegador não mostra notificações. No iPhone e no iPad: abra no Safari, toque em Compartilhar e em \"Adicionar à Tela de Início\"; depois abra o PharuERP pelo ícone e ligue os avisos aqui.",
  bloqueadas:
    "As notificações deste site estão bloqueadas no navegador. Libere no cadeado ao lado do endereço e volte aqui.",
  desligadas: "Avisa neste aparelho quando chegar mensagem, mesmo com o PharuERP fechado ou outra janela na frente.",
  ligadas: "Este aparelho avisa quando chegar mensagem, com a foto de quem mandou.",
};

export function GuardiaoDasNotificacoes() {
  const { authenticated, usuario, request, hasPermission, permissoes } = useAuth();
  const navigate = useNavigate();
  const usuarioId = usuario?.id || null;
  const permissoesCarregadas = Boolean(permissoes);
  const temChat = Boolean(authenticated) && hasPermission("chat:view");

  useEffect(() => {
    // Só decide com as permissões na mão: antes delas `temChat` é falso, e o
    // guardião soltava a inscrição a cada página aberta (o aviso daquele
    // intervalo se perdia).
    if (!authenticated || !usuarioId || !permissoesCarregadas) return;
    acertarComQuemEstaAqui(request, usuarioId, temChat);
    // `request` troca de identidade a cada render; quem decide é a pessoa.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authenticated, usuarioId, temChat, permissoesCarregadas]);

  // O botão do Chat, o sino e o menu recontam o chat por aqui, uma vez só
  // para o app inteiro (antes, só na volta de 60 s do sino): de tempos em
  // tempos com a aba visível, ao voltar para a aba e na hora em que o push
  // chega. Na página do Chat quem conta é o stream (`avisarNaoLidasDoChat`).
  const requestRef = useRef(request);
  requestRef.current = request;
  const { pathname } = useLocation();
  const naPaginaDoChat = pathname === "/chat" || pathname.startsWith("/chat/");
  useEffect(() => {
    if (!temChat || naPaginaDoChat) return undefined;
    const recontar = () => {
      if (document.visibilityState !== "visible") return;
      recontarNaoLidasDoChat(requestRef.current);
    };
    const batida = window.setInterval(recontar, RECONTAGEM_DO_CHAT_MS);
    document.addEventListener("visibilitychange", recontar);
    return () => {
      window.clearInterval(batida);
      document.removeEventListener("visibilitychange", recontar);
    };
  }, [temChat, naPaginaDoChat]);

  useEffect(() => {
    if (typeof navigator === "undefined" || !("serviceWorker" in navigator)) return undefined;
    const ouvir = (e) => {
      const dado = e.data;
      if (dado?.tipo === "abrir-conversa" && typeof dado.destino === "string" && dado.destino.startsWith("/chat")) {
        navigate(dado.destino);
      }
      if (dado?.tipo === "chat-novo") recontarNaoLidasDoChat(requestRef.current);
    };
    navigator.serviceWorker.addEventListener("message", ouvir);
    return () => navigator.serviceWorker.removeEventListener("message", ouvir);
  }, [navigate]);

  return null;
}

export function ControleDeNotificacoes() {
  const { usuario, request } = useAuth();
  const toast = useToast();
  const usuarioId = usuario?.id || null;
  const [estado, setEstado] = useState(null);
  const [ocupado, setOcupado] = useState(false);

  useEffect(() => {
    let vivo = true;
    setEstado(null);
    estadoAtual(usuarioId).then((e) => vivo && setEstado(e));
    return () => {
      vivo = false;
    };
  }, [usuarioId]);

  if (estado === null) return null;
  const impedido = estado === "sem_suporte" || estado === "bloqueadas";
  const ligada = estado === "ligadas";

  async function mudar() {
    setOcupado(true);
    try {
      const novo = ligada ? await desligar(request, usuarioId) : await ligar(request, usuarioId);
      setEstado(novo);
      if (!ligada && novo === "ligadas") toast.success("Notificações ligadas neste aparelho.");
      if (!ligada && novo === "bloqueadas") toast.error(EXPLICACAO.bloqueadas);
    } catch (erro) {
      toast.error(erro?.message || "Não deu para ligar as notificações.");
      setEstado(await estadoAtual(usuarioId));
    } finally {
      setOcupado(false);
    }
  }

  return (
    <div className="pchat-notificar" role="group" aria-label="Notificações do chat neste aparelho">
      <span className="pchat-notificar-texto">{EXPLICACAO[estado]}</span>
      {!impedido && (
        <button
          type="button"
          className="pchat-notificar-botao"
          onClick={mudar}
          disabled={ocupado}
          aria-pressed={ligada}
        >
          {ligada ? "Desligar avisos" : "Ligar avisos"}
        </button>
      )}
      {ligada && <ComONavegadorFechado />}
    </div>
  );
}

/**
 * Com o ERP fechado o aviso chega pelo navegador, que precisa seguir rodando
 * em segundo plano: sem isso, fechar a última janela desliga os avisos até o
 * navegador abrir de novo. Aqui fica o caminho exato do navegador em uso.
 */
function ComONavegadorFechado() {
  const toast = useToast();
  const navegador = navegadorDoComputador();
  if (!navegador) return null;
  const passo = SEGUNDO_PLANO[navegador];
  if (!passo) {
    return (
      <p className="pchat-notificar-fechado">
        Com o navegador totalmente fechado, nenhum site consegue avisar. Deixe-o aberto (pode ficar minimizado) ou use o Edge ou o Chrome.
      </p>
    );
  }
  const copiar = async () => {
    if (await copiarTexto(passo.endereco)) toast.success("Endereço copiado: cole na barra do navegador.");
    else toast.error(`Não deu para copiar. Digite ${passo.endereco} na barra do navegador.`);
  };
  return (
    <div className="pchat-notificar-fechado">
      <b>Para avisar com o ERP e o navegador fechados</b>
      <span>
        Em {passo.caminho}, ligue <q>{passo.opcao}</q>. O computador precisa estar ligado e com o Windows aberto.
      </span>
      <button type="button" className="pchat-notificar-botao" onClick={copiar}>Copiar {passo.endereco}</button>
    </div>
  );
}

const Convite = styled.div`
  display: inline-flex;
  align-items: center;
  min-height: 30px;
  border: 1px solid ${({ theme }) => theme.border};
  border-radius: 999px;
  background: ${({ theme }) => theme.surface};
  color: ${({ theme }) => theme.textSecondary};
  font-size: 11px;
  font-weight: 600;
  white-space: nowrap;

  button {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    border: 0;
    background: none;
    color: inherit;
    font: inherit;
    cursor: pointer;
    padding: 5px 4px 5px 11px;
  }

  button:hover:not(:disabled) {
    color: ${({ theme }) => theme.accent};
  }

  button + button {
    padding: 5px 9px 5px 4px;
  }

  @media (max-width: 860px) {
    display: none;
  }
`;

export function ConviteDosAvisosNoDesktop() {
  const { usuario, request } = useAuth();
  const toast = useToast();
  const usuarioId = usuario?.id || null;
  const [estado, setEstado] = useState(null);
  const [dispensado, setDispensado] = useState(() => dispensouConvite(usuarioId));
  const [ocupado, setOcupado] = useState(false);

  useEffect(() => {
    let vivo = true;
    setDispensado(dispensouConvite(usuarioId));
    const conferir = () => estadoAtual(usuarioId).then((e) => vivo && setEstado(e));
    conferir();
    window.addEventListener(EVENTO_AVISOS_MUDARAM, conferir);
    return () => {
      vivo = false;
      window.removeEventListener(EVENTO_AVISOS_MUDARAM, conferir);
    };
  }, [usuarioId]);

  // Para quem ainda não ligou, e para quem bloqueou no navegador (senão nunca
  // fica sabendo por que não chega aviso). Sem suporte se explica no Chat.
  const bloqueadas = estado === "bloqueadas";
  if ((estado !== "desligadas" && !bloqueadas) || dispensado) return null;

  async function ligarAgora() {
    if (bloqueadas) {
      toast.info(EXPLICACAO.bloqueadas);
      return;
    }
    setOcupado(true);
    try {
      const novo = await ligar(request, usuarioId);
      setEstado(novo);
      if (novo === "ligadas") toast.success("Pronto: as mensagens do chat vão aparecer na área de trabalho.");
      if (novo === "bloqueadas") toast.error(EXPLICACAO.bloqueadas);
    } catch (erro) {
      toast.error(erro?.message || "Não deu para ligar os avisos.");
    } finally {
      setOcupado(false);
    }
  }

  return (
    <Convite role="group" aria-label="Avisos do chat na área de trabalho">
      <button
        type="button"
        onClick={ligarAgora}
        disabled={ocupado}
        title={
          bloqueadas
            ? "O navegador está bloqueando os avisos deste site. Clique para ver como liberar."
            : "Mostra um aviso na área de trabalho quando chegar mensagem no Chat, mesmo com o PharuERP minimizado ou em outra tela."
        }
      >
        <FiBell size={12} />
        <span>{bloqueadas ? "Avisos do chat bloqueados" : "Ligar avisos do chat"}</span>
      </button>
      <button
        type="button"
        aria-label="Agora não"
        title="Agora não (dá para ligar depois, dentro do Chat)"
        onClick={() => {
          dispensarConvite(usuarioId);
          setDispensado(true);
        }}
      >
        <FiX size={12} />
      </button>
    </Convite>
  );
}
